// The volunteer's booking page, /book/:token (Contact Hub 4, Task 11; spec §4.4).
//
// Every action is a POST to bookingPage with the token in the body, never in a query string, never logged and never
// kept in browser storage (a shared phone). There is no hold: a time is free or taken, and the first Confirm wins. A
// loser sees "that time has just been taken" over the remaining free times on the same screen. The page shows the
// consent visit only (no follow-up dates, no clinic address, no calendar link) and never a name, panel number or study
// number: it shows only what the endpoint serves. The wording is the shared BOOKING_PAGE_TEXT, so it cannot drift from
// the server's.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PrivacyNoticeLink from './PrivacyNoticeLink';
import {
  BOOKING_PAGE_TEXT,
  BOOKING_PAYMENT_WARNING_TEXT,
  PAGE_REFRESH_SECONDS,
} from '../../api/src/functions/bookingOfferOptions';
import './BookingPage.css';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:7071';
const ENDPOINT = `${API_BASE}/api/bookingPage`;
const BOOKING_PRIVACY_PATH = '/privacy/booking';
/** How long the page waits before it asks again while the server is still finishing a booking. */
const BUSY_RETRY_MS = 3000;
/** How many times in a row the page asks again while the server answers busy, before it gives up. */
const BUSY_RETRY_LIMIT = 5;
/** How long one request may take before the page gives up on it and shows the error with Try again. */
const BOOKING_FETCH_TIMEOUT_MS = 20_000;

type Action = 'open' | 'confirm' | 'rearrange';

interface Time {
  slot: number;
  day: string;
  time: string;
  state: 'free' | 'taken';
}

type View =
  | { kind: 'loading' }
  | { kind: 'open'; studyTitle: string; expiresOn: string; paymentWarning: string; times: Time[]; selected: number | null; notice: string | null; acting: boolean }
  | { kind: 'booked'; studyTitle: string; day: string; time: string; paymentWarning: string; rearrangeRequested: boolean; acting: boolean }
  | { kind: 'busy' }
  | { kind: 'message'; text: string; retry: boolean };

interface Answer {
  status: number;
  body: Record<string, unknown>;
}

const ENDED_TEXT: Record<string, string> = {
  'all-gone': BOOKING_PAGE_TEXT.allGone,
  expired: BOOKING_PAGE_TEXT.expired,
  replaced: BOOKING_PAGE_TEXT.replaced,
  'opted-out': BOOKING_PAGE_TEXT.endedOptedOut,
  ended: BOOKING_PAGE_TEXT.ended,
  changed: BOOKING_PAGE_TEXT.changed,
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function parseTimes(v: unknown): Time[] | null {
  if (!Array.isArray(v)) return null;
  const out: Time[] = [];
  for (const t of v) {
    if (!t || typeof t !== 'object') return null;
    const r = t as Record<string, unknown>;
    if (typeof r.slot !== 'number' || typeof r.day !== 'string' || typeof r.time !== 'string') return null;
    out.push({ slot: r.slot, day: r.day, time: r.time, state: r.state === 'free' ? 'free' : 'taken' });
  }
  return out;
}

/** The open view for an answer that carries the times (state open, or a 409 taken / too-late). Null if malformed. */
function openViewFrom(body: Record<string, unknown>, notice: string | null): View | null {
  const times = parseTimes(body.times);
  if (!times) return null;
  return {
    kind: 'open',
    studyTitle: str(body.studyTitle),
    expiresOn: str(body.expiresOn),
    paymentWarning: str(body.paymentWarning) || BOOKING_PAYMENT_WARNING_TEXT,
    times,
    selected: null,
    notice,
    acting: false,
  };
}

function bookedViewFrom(body: Record<string, unknown>): View {
  return {
    kind: 'booked',
    studyTitle: str(body.studyTitle),
    day: str(body.day),
    time: str(body.time),
    paymentWarning: str(body.paymentWarning) || BOOKING_PAYMENT_WARNING_TEXT,
    rearrangeRequested: body.rearrangeRequested === true,
    acting: false,
  };
}

const failure = (text: string): View => ({ kind: 'message', text, retry: true });
const ending = (text: string): View => ({ kind: 'message', text, retry: false });

function groupByDay(times: Time[]): { day: string; times: Time[] }[] {
  const groups: { day: string; times: Time[] }[] = [];
  for (const t of times) {
    const last = groups[groups.length - 1];
    if (last && last.day === t.day) last.times.push(t);
    else groups.push({ day: t.day, times: [t] });
  }
  return groups;
}

function BookingPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<View>({ kind: 'loading' });

  // The newest request's number: an answer to an older request (a refresh that a Confirm overtook) is dropped.
  const seq = useRef(0);
  // True while a request the volunteer started is in flight, so a refresh never runs under it.
  const acting = useRef(false);
  const refreshing = useRef(false);
  const busyTries = useRef(0);
  const busyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const clearBusyTimer = useCallback(() => {
    if (busyTimer.current) {
      clearTimeout(busyTimer.current);
      busyTimer.current = null;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearBusyTimer();
    };
  }, [clearBusyTimer]);

  const post = useCallback(
    async (action: Action, slot?: number): Promise<Answer | null> => {
      // A request that hangs is aborted after BOOKING_FETCH_TIMEOUT_MS and reads as a network error (null), so the
      // buttons, the refresh and Try again are never blocked behind it. The abort also ends a body that never finishes.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), BOOKING_FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(slot === undefined ? { action, token } : { action, token, slot }),
          signal: controller.signal,
        });
        let body: Record<string, unknown> = {};
        try {
          const parsed: unknown = await res.json();
          if (parsed && typeof parsed === 'object') body = parsed as Record<string, unknown>;
        } catch {
          if (controller.signal.aborted) return null;
          body = {};
        }
        return { status: res.status, body };
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    },
    [token]
  );

  /** What an answer means for the page, whatever the action. `null` means "not an ending or failure: the caller decides". */
  const commonView = (a: Answer | null): View | 'busy' | null => {
    if (a === null) return failure(BOOKING_PAGE_TEXT.error);
    if (a.status === 404) return ending(BOOKING_PAGE_TEXT.notFound);
    if (a.status === 429) return failure(BOOKING_PAGE_TEXT.rateLimited);
    if (a.status === 410) return ending(ENDED_TEXT[str(a.body.page)] ?? BOOKING_PAGE_TEXT.ended);
    if (a.status === 200 && a.body.state === 'busy') return 'busy';
    if (a.status === 409 && a.body.error === 'busy') return 'busy';
    if (a.status === 409 && a.body.error === 'needs-staff') return ending(BOOKING_PAGE_TEXT.needsStaff);
    return null;
  };

  // run and settle call each other (a busy answer asks again after 3 s); a ref breaks the cycle.
  const runRef = useRef<(action: Action, slot?: number) => Promise<void>>(async () => undefined);

  const settle = useCallback(
    (a: Answer | null, action: Action) => {
      const common = commonView(a);
      if (common === 'busy') {
        if (busyTries.current >= BUSY_RETRY_LIMIT) {
          setView(failure(BOOKING_PAGE_TEXT.error));
          return;
        }
        busyTries.current += 1;
        setView({ kind: 'busy' });
        clearBusyTimer();
        busyTimer.current = setTimeout(() => {
          busyTimer.current = null;
          void runRef.current('open');
        }, BUSY_RETRY_MS);
        return;
      }
      busyTries.current = 0;
      if (common) {
        setView(common);
        return;
      }
      const answer = a as Answer;
      if (answer.status === 200 && answer.body.state === 'booked') {
        setView(bookedViewFrom(answer.body));
        return;
      }
      if (answer.status === 200 && answer.body.state === 'open') {
        setView(openViewFrom(answer.body, null) ?? failure(BOOKING_PAGE_TEXT.error));
        return;
      }
      if (action === 'confirm' && answer.status === 409 && (answer.body.error === 'taken' || answer.body.error === 'too-late')) {
        const notice = answer.body.error === 'taken' ? BOOKING_PAGE_TEXT.justTaken : BOOKING_PAGE_TEXT.tooLate;
        setView(openViewFrom(answer.body, notice) ?? failure(BOOKING_PAGE_TEXT.error));
        return;
      }
      setView(failure(BOOKING_PAGE_TEXT.error));
    },
    [clearBusyTimer]
  );

  /** A request the volunteer (or the page's own retry) started. */
  const run = useCallback(
    async (action: Action, slot?: number) => {
      if (acting.current) return;
      acting.current = true;
      clearBusyTimer();
      const id = ++seq.current;
      setView((v) => (v.kind === 'open' || v.kind === 'booked' ? { ...v, acting: true } : v));
      let a: Answer | null = null;
      try {
        a = await post(action, slot);
      } finally {
        acting.current = false;
      }
      if (!mounted.current || id !== seq.current) return;
      settle(a, action);
    },
    [post, settle, clearBusyTimer]
  );
  runRef.current = run;

  /** The volunteer's own Try again: a fresh busy budget, so a booking still finishing is waited for again. */
  const tryAgain = useCallback(() => {
    busyTries.current = 0;
    void run('open');
  }, [run]);

  // The first open.
  useEffect(() => {
    if (!token) {
      setView(ending(BOOKING_PAGE_TEXT.notFound));
      return;
    }
    void run('open');
    // Only when the token changes: a re-render must never open again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /** A quiet re-read of the times: failures and busy answers are ignored (the next tick asks again). */
  const refresh = useCallback(async () => {
    if (acting.current || refreshing.current) return;
    refreshing.current = true;
    const id = ++seq.current;
    let a: Answer | null = null;
    try {
      a = await post('open');
    } finally {
      refreshing.current = false;
    }
    if (!mounted.current || id !== seq.current || a === null) return;
    const common = commonView(a);
    if (common === 'busy') return;
    if (common) {
      // An ending (404, 410) replaces the page; a 429 or a failure leaves it as it is.
      if (common.kind === 'message' && !common.retry) setView((v) => (v.kind === 'open' ? common : v));
      return;
    }
    if (a.status === 200 && a.body.state === 'booked') {
      setView((v) => (v.kind === 'open' ? bookedViewFrom(a.body) : v));
      return;
    }
    if (a.status === 200 && a.body.state === 'open') {
      const fresh = openViewFrom(a.body, null);
      if (!fresh || fresh.kind !== 'open') return;
      setView((v) => {
        if (v.kind !== 'open' || v.acting) return v;
        const stillFree = v.selected === null || fresh.times.some((t) => t.slot === v.selected && t.state === 'free');
        return {
          ...fresh,
          selected: stillFree ? v.selected : null,
          notice: stillFree ? v.notice : BOOKING_PAGE_TEXT.justTaken,
        };
      });
    }
  }, [post]);

  // Re-open every PAGE_REFRESH_SECONDS while the page is open and the document is visible; pause when hidden.
  const isOpen = view.kind === 'open';
  useEffect(() => {
    if (!isOpen) return undefined;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      void refresh();
    };
    const timer = setInterval(tick, PAGE_REFRESH_SECONDS * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isOpen, refresh]);

  const choose = (slot: number | null) =>
    setView((v) => (v.kind === 'open' && !v.acting ? { ...v, selected: slot, notice: null } : v));

  const renderOpen = (v: Extract<View, { kind: 'open' }>) => {
    const chosen = v.selected === null ? null : v.times.find((t) => t.slot === v.selected) ?? null;
    return (
      <>
        <div className="form-header">
          <h1>{BOOKING_PAGE_TEXT.heading}</h1>
          <p>{`for our ${v.studyTitle} study`}</p>
        </div>
        <div className="form-body">
          {v.notice && (
            <div className="booking-notice" role="alert">
              {v.notice}
            </div>
          )}
          {chosen ? (
            <div className="booking-confirm">
              <h2 className="booking-confirm-title">{`Book ${chosen.day} at ${chosen.time}?`}</h2>
              <p className="screening-payment">{v.paymentWarning}</p>
              <button type="button" className="submit-btn" disabled={v.acting} onClick={() => void run('confirm', chosen.slot)}>
                Confirm booking
              </button>
              <button type="button" className="screening-nav-btn booking-secondary" disabled={v.acting} onClick={() => choose(null)}>
                Choose a different time
              </button>
            </div>
          ) : (
            <>
              <p className="booking-first-come">{BOOKING_PAGE_TEXT.firstCome}</p>
              <p className="booking-expiry">{`This link works until ${v.expiresOn}.`}</p>
              {groupByDay(v.times).map((group) => (
                <section key={group.day} className="booking-day">
                  <h2 className="booking-day-title">{group.day}</h2>
                  <div className="booking-times">
                    {group.times.map((t) => (
                      <button
                        key={t.slot}
                        type="button"
                        className={t.state === 'taken' ? 'booking-time-btn taken' : 'booking-time-btn'}
                        disabled={t.state === 'taken' || v.acting}
                        onClick={() => choose(t.slot)}
                      >
                        <span className="booking-time-label">{t.time}</span>
                        {t.state === 'taken' && <> <span className="booking-time-taken">Just taken</span></>}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>
      </>
    );
  };

  const renderBooked = (v: Extract<View, { kind: 'booked' }>) => (
    <>
      <div className="form-header">
        <h1>{BOOKING_PAGE_TEXT.bookedHeading}</h1>
        <p>{`for our ${v.studyTitle} study`}</p>
      </div>
      <div className="form-body">
        <p className="booking-when">
          <span className="booking-when-day">{v.day}</span> at <span className="booking-when-time">{v.time}</span>
        </p>
        <p className="screening-payment">{v.paymentWarning}</p>
        {v.rearrangeRequested ? (
          <p className="booking-rearrange-done">{BOOKING_PAGE_TEXT.rearrangeDone}</p>
        ) : (
          <button type="button" className="screening-nav-btn booking-secondary" disabled={v.acting} onClick={() => void run('rearrange')}>
            {BOOKING_PAGE_TEXT.rearrangeButton}
          </button>
        )}
      </div>
    </>
  );

  let content: JSX.Element;
  switch (view.kind) {
    case 'open':
      content = renderOpen(view);
      break;
    case 'booked':
      content = renderBooked(view);
      break;
    case 'busy':
      content = (
        <div className="form-body">
          <p>{BOOKING_PAGE_TEXT.busy}</p>
        </div>
      );
      break;
    case 'message':
      content = (
        <div className="form-body">
          {view.retry ? <div className="error-message">{view.text}</div> : <p className="booking-ended">{view.text}</p>}
          {view.retry && (
            <button type="button" className="submit-btn" onClick={tryAgain}>
              Try again
            </button>
          )}
        </div>
      );
      break;
    default:
      content = (
        <div className="form-body">
          <p>Loading...</p>
        </div>
      );
  }

  return (
    <div className="form-container">
      {content}
      <div className="form-body booking-privacy">
        <PrivacyNoticeLink href={BOOKING_PRIVACY_PATH} />
      </div>
    </div>
  );
}

export default BookingPage;
